/**
 * 部署后用户无感知刷新。
 *
 * 背景：护士站大屏和护士的 PC 浏览器会连续数天不关页面，而每次发版都是
 * "CI 改镜像 tag → ArgoCD/kubectl 原地替换 nginx pod"。用户的浏览器不会被通知，
 * 于是要么长期停在旧版本，要么点一次导航就撞上旧 chunk 404。
 *
 * 本模块让页面自己在合适的时机静默 reload 到新版本：
 *
 *   每 5min 探测 /version.json → 发现不一致 → 记下"待刷新"与起始时刻（cap 计时起点）
 *     → 等待合适时机：标签页隐藏 ‖ 用户空闲 60s ‖ 距首次发现已 ≥ 30min
 *     → 停语音 / 关弹窗 / 记账本 → location.reload()
 *
 * 三条贯穿全局的约束：
 * 1. **完全静默**：不出现任何提示 UI，只用 console 留痕（前缀 [auto-refresh]）。
 * 2. **不打断正在处理的事**：告警弹窗、语音播报、提交在途是 HARD 阻塞，绝不越过；
 *    只有"同一个 HARD 阻塞被持有超过 hardBlockMaxMs"才兜底强制越过（否则版本永不更新）。
 * 3. **绝不刷新循环**：账本（reloadLedger）限制冷却、同目标次数与窗口内总量。
 *
 * 参数见 src/settings/autoRefreshSetting.ts；总开关是编译期常量 __AUTO_REFRESH_ENABLED__。
 */

import { useIdle } from '@vueuse/core';
import type { Router } from 'vue-router';

import {
  AUTO_REFRESH_DEFAULTS,
  getAutoRefreshSetting,
  type AutoRefreshSetting,
} from '/@/settings/autoRefreshSetting';
import { useUserStoreWithOut } from '/@/store/modules/user';
import { emitter, WS_EVENTS } from '/@/utils/wx/tkSetupStomp';
import { checkRemoteVersion } from '/@/utils/autoRefresh/versionChecker';
import {
  clearAllBlockers,
  getBlockers,
  getGuardStats,
  getOldestHardBlockerSince,
  hasSoftBlocker,
  hasVisibleDialog,
} from '/@/utils/autoRefresh/refreshGuard';
import {
  enterBackoff,
  evaluateReloadPermission,
  getBackoffRemainingMs,
  recordReloadAttempt,
  resetLedgers,
} from '/@/utils/autoRefresh/reloadLedger';
import { prepareForReload, reloadNow } from '/@/utils/autoRefresh/prepareReload';

const LOG = '[auto-refresh]';

/** 进入 /login 的判定值与 permissionGuard 的白名单保持一致 */
const LOGIN_PATH = '/login';

type RefreshSource = 'idle' | 'hidden' | 'cap';

let started = false;
let checking = false;
let refreshing = false;
let pollTimer: TimeoutHandle | null = null;
let lastTriggerAt = 0;
let currentRouter: Router | null = null;

/** 待刷新的目标版本；null 表示当前页面已是最新 */
let pendingVersion: string | null = null;
/** 首次探测到该目标版本的时刻 —— cap 的计时起点（不是页面加载时刻）*/
let pendingSince = 0;

/**
 * 用户最后一次活动时刻。
 *
 * 刻意只取 useIdle 的 lastActive，而不是它的 idle ref：idle 的阈值在创建时固定，
 * 而调试覆盖（localStorage.__AR__OVERRIDE__）需要能改 idleMs 做现场验证。
 * 事件监听（mousemove/keydown/wheel/touchstart/resize…）复用 useIdle 的实现。
 */
const { lastActive } = useIdle(AUTO_REFRESH_DEFAULTS.idleMs, { initialState: false });

/**
 * 启动。在 main.ts 中于 `router.isReady()` 之前调用——
 * 首屏进入懒路由时会立刻触发 chunk 加载，注册晚了会漏掉那一次。
 */
export function setupAutoRefresh(router: Router): void {
  if (started) return;

  // 编译期常量，dev 下恒为 false（vite.config.ts 用 isBuild 一票否决）。
  // dev 里 public/version.json 已删除、探测必然 404，这里再挡一道，
  // 避免将来有人改坏探测逻辑后本地开发被反复 reload。
  if (!__AUTO_REFRESH_ENABLED__) return;

  started = true;
  currentRouter = router;

  const cfg = getAutoRefreshSetting();
  console.info(
    `${LOG} 已启用：当前版本 ${__APP_VERSION__}，轮询 ${cfg.pollMs / 1000}s，` +
      `空闲阈值 ${cfg.idleMs / 1000}s，最长等待 ${cfg.capMs / 60000}min`,
  );

  installTriggers();
  // 启动 30s 后再做首次探测：错开应用自身的启动请求（userInfo/菜单/WS 建连），
  // 同时能很快抓到"拿到了一份被缓存的旧 HTML"这种情况。
  scheduleNextCheck(30 * 1000);
  runGuardSelfCheck();
  exposeDebugHandle();
}

function installTriggers(): void {
  const onTrigger = (source: string) => {
    const cfg = getAutoRefreshSetting();
    const now = Date.now();
    // 切换标签页/网络恢复会连续触发多个事件，必须节流，否则会把探测接口打成轮询
    if (now - lastTriggerAt < cfg.focusMinIntervalMs) return;
    lastTriggerAt = now;
    void checkOnce(source);
  };

  // 变为可见时立刻确认（切回来就该是最新版）；变为隐藏时同样触发，
  // 因为"隐藏"本身是执行刷新的最佳时机（见 decideSource）。
  document.addEventListener('visibilitychange', () => onTrigger('visibilitychange'));
  window.addEventListener('focus', () => onTrigger('focus'));
  window.addEventListener('online', () => onTrigger('online'));
  // WS 重连：发版常与后端重启同批发生，是一个高性价比的"发版信号"
  emitter.on(WS_EVENTS.RECONNECT, () => onTrigger('ws-reconnect'));
}

function scheduleNextCheck(delay?: number): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }

  const cfg = getAutoRefreshSetting();
  // 处于退避中说明刷新被次数闸拒绝过（多半是代理缓存或发布期 pod 横跳），
  // 降频等待窗口自然消失，而不是继续按 pollMs 反复试探。
  const backoffMs = getBackoffRemainingMs(Date.now());
  const wait = delay ?? (backoffMs > 0 ? Math.min(backoffMs, cfg.backoffMs) : cfg.pollMs);

  pollTimer = setTimeout(() => {
    void checkOnce('poll');
  }, wait);
}

/** 登录页/无 token 时跳过整轮判定，且不推进 cap 计时 */
function isCheckEligible(): boolean {
  if (!currentRouter) return false;
  // 落到 /login 会让 stateGuard 清空 5 个 store（见 router/guard/stateGuard.ts），
  // 在登录页刷新既无用又有害
  if (currentRouter.currentRoute.value.path === LOGIN_PATH) return false;
  // 无 token 时 reload 同样会落到登录页
  if (!useUserStoreWithOut().getToken) return false;
  return true;
}

async function checkOnce(trigger: string): Promise<void> {
  if (checking || refreshing) return;

  checking = true;
  try {
    if (!isCheckEligible()) return;

    const cfg = getAutoRefreshSetting();
    const outcome = await checkRemoteVersion(cfg.fetchTimeoutMs);

    if (outcome.result !== 'changed') {
      if (pendingVersion) {
        console.info(`${LOG} 版本已对齐，取消待刷新`);
        pendingVersion = null;
        pendingSince = 0;
      }
      return;
    }

    const target = outcome.version as string;
    if (pendingVersion !== target) {
      pendingVersion = target;
      pendingSince = Date.now();
      console.info(`${LOG} 发现新版本 ${target}（当前 ${__APP_VERSION__}），等待合适时机刷新`);
    }

    maybeReload(cfg, trigger);
  } catch (error) {
    // 走到这里且 refreshing 为真，说明是 reloadNow() 抛了错——刷新并没有发生，页面还活着。
    // 必须解除 refreshing，否则后续每次检查都会被前置守卫挡掉，功能彻底停摆。
    if (refreshing) {
      refreshing = false;
      console.error(`${LOG} 刷新失败，已恢复轮询`, error);
    } else {
      console.warn(`${LOG} 版本检查异常（已忽略）`, error);
    }
  } finally {
    checking = false;
    scheduleNextCheck();
  }
}

/**
 * 时机判定。返回 null 表示"现在不宜刷新"。
 * 顺序即优先级，注释里的编号就是取舍逻辑。
 */
function decideSource(cfg: AutoRefreshSetting, now: number): RefreshSource | null {
  const hardSince = getOldestHardBlockerSince();

  if (hardSince !== null) {
    // ① 同目标次数/窗口闸不做处理，只管"该不该等" —— HARD 阻塞默认永不越过。
    //    但一个被长期持有的 HARD（告警弹窗开了半小时无人处置、语音 count=-1 无限循环、
    //    某个提交请求挂死）会让版本永远不更新，所以必须有兜底。
    if (now - hardSince < cfg.hardBlockMaxMs) return null;
    console.warn(
      `${LOG} 硬阻塞已持有 ${Math.round((now - hardSince) / 60000)} 分钟，强制清理后刷新`,
    );
    return 'cap';
  }

  // ② 标签页隐藏：用户完全看不到，是"静默"的最优落点，无视 SOFT 阻塞。
  //    （语音播报在 ① 已被 HARD 拦住 —— 后台标签页里 speechSynthesis 照样在响。）
  if (document.hidden) return 'hidden';

  // ③ 有可见弹窗（含漏接弹窗的通用兜底网）或 SOFT 阻塞 → 用户可能正在看着它 /
  //    等着某个结果（如 AI 长流式输出），不走空闲路径，等 cap
  if (hasVisibleDialog() || hasSoftBlocker()) {
    if (now - pendingSince >= cfg.capMs) return 'cap';
    return null;
  }

  // ④ 用户无操作达到阈值 → 空闲，刷新最不容易被察觉
  if (now - lastActive.value >= cfg.idleMs) return 'idle';

  // ⑤ 超过最长等待上限：不再等空闲，只要不是硬阻塞就刷
  if (now - pendingSince >= cfg.capMs) return 'cap';

  return null;
}

function maybeReload(cfg: AutoRefreshSetting, trigger: string): void {
  const now = Date.now();
  const source = decideSource(cfg, now);
  if (!source) return;

  const target = pendingVersion as string;
  const gate = evaluateReloadPermission(target, cfg, now);
  if (!gate.allowed) {
    console.error(`${LOG} 放弃本次刷新：${gate.reason}`);
    if (gate.backoff) enterBackoff(cfg, now);
    return;
  }

  refreshing = true;
  // 先落账本：sessionStorage 在 reload 之后仍可读，这也是防循环的唯一依据
  recordReloadAttempt(target, cfg, now);
  console.info(
    `${LOG} 执行无感刷新：${__APP_VERSION__} → ${target}（时机 ${source}，触发 ${trigger}）`,
  );

  prepareForReload(source === 'cap');
  reloadNow();
}

/**
 * 接入自检（对应风险 R4）：HARD 保护依赖组件侧的 holdRefresh 调用。
 * 若启动后一段时间内一次都没观测到登记，说明接入点被改坏了
 * （例如 WarningBoxModal 换了弹窗实现），保护会静默退化成 SOFT 兜底网。
 */
function runGuardSelfCheck(): void {
  setTimeout(() => {
    if (getGuardStats().holdEvents === 0) {
      console.warn(`${LOG} 启动 30s 内未观测到任何阻塞登记，HARD 保护可能已失效（接入点被改动？）`);
    }
  }, 30 * 1000);
}

/**
 * 现场调试句柄。功能是完全静默的，只有日志，所以需要一个能在控制台
 * 查状态、手动触发、清账本的入口。配合 localStorage.__AR__OVERRIDE__ 调参使用。
 */
function exposeDebugHandle(): void {
  (window as Recordable).__autoRefresh = {
    version: __APP_VERSION__,
    setting: getAutoRefreshSetting,
    checkNow: (trigger = 'manual') => checkOnce(trigger),
    getState: () => ({
      pendingVersion,
      pendingSince,
      blockers: getBlockers(),
      backoffRemainingMs: getBackoffRemainingMs(Date.now()),
      idleForMs: Date.now() - lastActive.value,
      hidden: document.hidden,
    }),
    reset: () => {
      resetLedgers();
      clearAllBlockers();
      pendingVersion = null;
      pendingSince = 0;
    },
  };
}
